// Shared MCQ matching for the Check screen.
// One working copy of sheet rows is matched live against the imported
// student list. A sheet stays on the flat problem grid until it uniquely
// matches a student and its roll / variant / name cross-checks are clean.
// Absentees (students with no sheet) do not block that switch — they show
// as empty rows on the class table.

const ROLL_FORMAT_RE = /^[A-Za-z][0-9]{2}$/;

export function isBadRollFormat(v) {
  const s = (v == null ? '' : String(v)).trim();
  return s.length > 0 && !ROLL_FORMAT_RE.test(s);
}

export function extractRollFromName(name) {
  if (!name) return null;
  const m = String(name).match(/\(([^)]+)\)/);
  return m ? m[1].trim().toLowerCase() : null;
}

export function studentRoll(student) {
  const name = String(student?.student_name || '');
  const lead = name.match(/^\s*\(([^)]+)\)/);
  if (lead) return lead[1].trim().toUpperCase();
  const any = name.match(/\(([^)]+)\)/);
  if (any) return any[1].trim().toUpperCase();
  return String(student?.roll_number || '').trim().toUpperCase();
}

export function sheetRoll(row) {
  return String(row?.roll_number || '').trim().toUpperCase();
}

export function stripRollFromName(name) {
  if (!name) return '';
  return String(name)
    .replace(/\([^)]+\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeNameForCompare(n) {
  return String(n || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
}

export function formatListLabel(cls, sec, subj) {
  const c = (cls || '').toString().trim();
  const s = (sec || '').toString().trim();
  const sub = (subj || '').toString().trim();
  const sectionAlreadyInClass = s && c.toUpperCase().endsWith(s.toUpperCase());
  const parts = [c];
  if (s && !sectionAlreadyInClass) parts.push(s);
  if (sub) parts.push(sub);
  return parts.filter(Boolean).join(' - ');
}

export function buildComment(cleanName, imageName, absent = false) {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const dateStr = `${dd}/${mm}/${yyyy}`;
  const parts = [];
  const name = (cleanName || '').trim();
  if (name) parts.push(name);
  parts.push(dateStr);
  const img = (imageName || '').trim();
  if (img && img !== '(filled)') parts.push(img);
  if (absent) parts.push('Absent');
  return parts.join(' ');
}

export function isAbsent(matched, count) {
  if (!matched || !matched.answers) return true;
  for (let q = 1; q <= count; q++) {
    const v = matched.answers[String(q)];
    if (v != null && /[A-Da-d]/.test(String(v))) return false;
  }
  return true;
}

export function needsEvaluation(answers, count) {
  if (!answers) return true;
  for (let q = 1; q <= count; q++) {
    const v = answers[String(q)];
    if (v == null) return true;
    if (!/[A-Da-d]/.test(String(v))) return true;
  }
  return false;
}

export function isAbsentRow(row) {
  return !!(row && row.answers && row.answers._absent);
}

function variantPastMax(value, maxVariant) {
  if (!maxVariant) return false;
  const ch = String(value || '').trim().toUpperCase()[0];
  if (!ch || !/[A-Z]/.test(ch)) return false;
  return ch > maxVariant;
}

export const PROBLEM_LABELS = {
  duplicate: 'duplicate roll',
  'no-student': 'no student',
  'no-roll': 'no roll',
  'user-unmatched': 'unmatched',
  name: 'name mismatch',
  variant: 'variant',
  'variant-max': 'variant past max',
  'roll-check': 'roll mismatch'
};

const UNMATCHED_STORAGE_KEY = 'mcqUserUnmatchedByFolder';

export function loadUnmatched(folderId) {
  if (!folderId) return new Set();
  try {
    const map = JSON.parse(localStorage.getItem(UNMATCHED_STORAGE_KEY) || '{}');
    const ids = map[String(folderId)] || [];
    return new Set(ids.map(String));
  } catch (_) {
    return new Set();
  }
}

export function persistUnmatched(folderId, idSet) {
  if (!folderId) return;
  try {
    const map = JSON.parse(localStorage.getItem(UNMATCHED_STORAGE_KEY) || '{}');
    map[String(folderId)] = [...idSet];
    localStorage.setItem(UNMATCHED_STORAGE_KEY, JSON.stringify(map));
  } catch (_) { /* quota — ignore */ }
}

export function matchSheets(rows, students, unmatchedIds) {
  const unmatchedSet = unmatchedIds instanceof Set ? unmatchedIds : new Set(unmatchedIds || []);
  const byRoll = new Map();
  for (const row of rows || []) {
    if (unmatchedSet.has(String(row.id))) continue;
    const roll = sheetRoll(row);
    if (!roll) continue;
    const arr = byRoll.get(roll) || [];
    arr.push(row);
    byRoll.set(roll, arr);
  }

  const byStudent = new Map();
  const matchedRowIds = new Set();
  const duplicates = new Map();
  const reasonByRowId = new Map();
  const studentById = new Map();
  for (const s of students || []) studentById.set(s.id, s);

  for (const s of students || []) {
    const sRoll = studentRoll(s);
    if (!sRoll) continue;
    const hits = byRoll.get(sRoll);
    if (!hits || hits.length === 0) continue;
    if (hits.length > 1) {
      duplicates.set(sRoll, hits.length);
      hits.forEach(h => reasonByRowId.set(String(h.id), 'duplicate'));
      continue;
    }
    byStudent.set(s.id, hits[0]);
    matchedRowIds.add(String(hits[0].id));
  }

  const unmatched = (rows || []).filter(r => !matchedRowIds.has(String(r.id)));
  unmatched.forEach(r => {
    const id = String(r.id);
    if (reasonByRowId.has(id)) return;
    if (unmatchedSet.has(id)) reasonByRowId.set(id, 'user-unmatched');
    else if (!sheetRoll(r)) reasonByRowId.set(id, 'no-roll');
    else reasonByRowId.set(id, 'no-student');
  });

  return { byStudent, unmatched, duplicates, reasonByRowId, studentById };
}

// Sort-tab style identity match. A sheet is matched by its bubbled roll.
// Name mismatches and duplicate rolls stay on the imported-list pane.
// Rolls that match no student, blank rolls, and manually unmatched sheets
// stay on the unmatched pane. Students with no sheet are "missing" and do
// not block the class table — they become absent rows there.
export function buildSheetReconciliation(rows, students, unmatchedIds) {
  const unmatchedSet = unmatchedIds instanceof Set ? unmatchedIds : new Set(unmatchedIds || []);
  const byRoll = new Map();
  const noRoll = [];
  const forced = [];
  for (const row of rows || []) {
    if (unmatchedSet.has(String(row.id))) {
      forced.push(row);
      continue;
    }
    const roll = sheetRoll(row);
    if (!roll) {
      noRoll.push(row);
      continue;
    }
    const arr = byRoll.get(roll) || [];
    arr.push(row);
    byRoll.set(roll, arr);
  }

  const matched = [];
  const missingStudents = [];
  const usedRolls = new Set();
  let nameMismatchCount = 0;
  let duplicateCount = 0;

  for (const stu of students || []) {
    const roll = studentRoll(stu);
    const expectedName = normalizeNameForCompare(stripRollFromName(stu.student_name));
    const hits = roll ? byRoll.get(roll) : null;
    if (!hits || hits.length === 0) {
      missingStudents.push(stu);
      continue;
    }
    const isDuplicate = hits.length > 1;
    if (isDuplicate) duplicateCount += hits.length;
    const annotated = hits.map(r => {
      const got = normalizeNameForCompare(stripRollFromName(r.student_name));
      const nameMismatch = !!expectedName && got !== expectedName;
      if (nameMismatch) nameMismatchCount += 1;
      return { ...r, _nameMismatch: nameMismatch, _isDuplicateRoll: isDuplicate };
    });
    matched.push({ expected: stu, aiRows: annotated });
    usedRolls.add(roll);
  }

  const unmatchedResults = [...forced, ...noRoll];
  for (const [roll, rs] of byRoll.entries()) {
    if (usedRolls.has(roll)) continue;
    const isDuplicate = rs.length > 1;
    for (const r of rs) unmatchedResults.push({ ...r, _isDuplicateRoll: isDuplicate });
  }

  return { matched, missingStudents, unmatchedResults, nameMismatchCount, duplicateCount };
}

function crossCheckReasons(row, maxVariant) {
  if (isAbsentRow(row)) return [];
  const reasons = [];
  const hwVar = String(row.handwritten_paper_variant || '').trim().toUpperCase();
  const bubVar = String(row.paper_variant || '').trim().toUpperCase();
  if (!hwVar || !bubVar || hwVar !== bubVar) reasons.push('variant');
  else if (variantPastMax(bubVar, maxVariant)) reasons.push('variant-max');
  const hwRoll = String(row.handwritten_roll_number || '').trim().toUpperCase();
  const bubRoll = String(row.roll_number || '').trim().toUpperCase();
  if (!hwRoll || !bubRoll || hwRoll !== bubRoll || isBadRollFormat(hwRoll) || isBadRollFormat(bubRoll)) {
    reasons.push('roll-check');
  }
  return reasons;
}

// problemById: rowId -> { reasons: string[], expectedName: string }
export function analyzeSheets(rows, students, unmatchedIds, maxVariant) {
  const match = matchSheets(rows, students, unmatchedIds);
  const problemById = new Map();
  const add = (id, reason, expectedName) => {
    const key = String(id);
    const cur = problemById.get(key) || { reasons: [], expectedName: '' };
    if (reason && !cur.reasons.includes(reason)) cur.reasons.push(reason);
    if (expectedName) cur.expectedName = expectedName;
    problemById.set(key, cur);
  };

  for (const row of match.unmatched) {
    add(row.id, match.reasonByRowId.get(String(row.id)) || 'no-student');
  }

  for (const [studentId, row] of match.byStudent.entries()) {
    const student = match.studentById.get(studentId);
    if (!student) continue;
    const expected = stripRollFromName(student.student_name);
    const got = stripRollFromName(row.student_name);
    if (expected && normalizeNameForCompare(got) !== normalizeNameForCompare(expected)) {
      add(row.id, 'name', expected);
    }
  }

  for (const row of rows || []) {
    for (const reason of crossCheckReasons(row, maxVariant)) add(row.id, reason);
  }

  return { match, problemById };
}

export function summarizeProblems(problemById) {
  const counts = {};
  for (const info of problemById.values()) {
    for (const reason of info.reasons) counts[reason] = (counts[reason] || 0) + 1;
  }
  return counts;
}

export function applyMarkAbsent(rows, students, match, mcqCount) {
  const count = mcqCount;
  let filledVariants = 0;
  let filledAnswers = 0;
  const nextRows = (rows || []).map(r => {
    const ans = { ...(r.answers || {}) };
    let touchedAns = false;
    for (let q = 1; q <= count; q++) {
      const k = String(q);
      const v = ans[k];
      const blank = v == null || (typeof v === 'string' && v.trim() === '');
      if (blank) {
        ans[k] = '.';
        touchedAns = true;
        filledAnswers++;
      }
    }
    const variantBlank = !r.paper_variant || !String(r.paper_variant).trim();
    if (variantBlank) filledVariants++;
    const nextVariant = variantBlank ? 'A' : r.paper_variant;
    const hwBlank = !r.handwritten_paper_variant || !String(r.handwritten_paper_variant).trim();
    return {
      ...r,
      paper_variant: nextVariant,
      handwritten_paper_variant: hwBlank ? nextVariant : r.handwritten_paper_variant,
      answers: touchedAns ? ans : r.answers
    };
  });

  let synthesized = 0;
  if (students && students.length > 0 && match) {
    const matchedStudentIds = new Set(match.byStudent.keys());
    const blankAnswers = { _absent: true };
    for (let q = 1; q <= count; q++) blankAnswers[String(q)] = '.';
    students.forEach(s => {
      if (matchedStudentIds.has(s.id)) return;
      const sRoll = studentRoll(s);
      if (!sRoll) return;
      const already = nextRows.some(r => sheetRoll(r) === sRoll);
      if (already) return;
      const shortClass = String(s.class || '').slice(0, 2);
      nextRows.push({
        id: `synth-${s.id}-${Date.now()}`,
        omr_scan_id: null,
        text_scan_id: null,
        omr_file_name: '(filled)',
        text_file_name: '',
        roll_number: sRoll,
        handwritten_roll_number: sRoll,
        class: shortClass,
        section: s.section || '',
        paper_variant: 'A',
        handwritten_paper_variant: 'A',
        subject: s.subject || '',
        student_name: stripRollFromName(s.student_name),
        answers: { ...blankAnswers }
      });
      synthesized++;
    });
  }

  return { rows: nextRows, filledVariants, filledAnswers, synthesized };
}

const MIGRATED_KEY = 'mcqApproveMigratedFolders';

export function isApproveMigrated(folderId) {
  if (!folderId) return true;
  try {
    const map = JSON.parse(localStorage.getItem(MIGRATED_KEY) || '{}');
    return !!map[String(folderId)];
  } catch (_) {
    return false;
  }
}

export function markApproveMigrated(folderId) {
  if (!folderId) return;
  try {
    const map = JSON.parse(localStorage.getItem(MIGRATED_KEY) || '{}');
    map[String(folderId)] = true;
    localStorage.setItem(MIGRATED_KEY, JSON.stringify(map));
  } catch (_) { /* quota — ignore */ }
}

// Rows that existed only on the old Approve tab (absent placeholders, or a
// roll the grader corrected there) are copied onto the Check rows once.
export function mergeServerApprove(checkRows, approveRows) {
  const next = (checkRows || []).slice();
  const byScan = new Map();
  next.forEach((r, i) => {
    if (r.omr_scan_id != null) byScan.set(String(r.omr_scan_id), i);
  });
  for (const a of approveRows || []) {
    if (a.omr_scan_id == null) {
      const roll = String(a.roll_number || '').trim().toUpperCase();
      if (!roll) continue;
      if (next.some(r => String(r.roll_number || '').trim().toUpperCase() === roll)) continue;
      next.push({
        id: a.id || `synth-migrated-${roll}`,
        omr_scan_id: null,
        text_scan_id: null,
        omr_file_name: '(filled)',
        text_file_name: '',
        student_name: a.student_name || '',
        handwritten_paper_variant: a.handwritten_paper_variant || a.paper_variant || 'A',
        handwritten_roll_number: a.handwritten_roll_number || a.roll_number || roll,
        class: a.class || '',
        section: a.section || '',
        subject: a.subject || '',
        roll_number: a.roll_number || roll,
        paper_variant: a.paper_variant || 'A',
        answers: { ...(a.answers || {}), _absent: true }
      });
      continue;
    }
    const idx = byScan.get(String(a.omr_scan_id));
    if (idx == null) continue;
    const cur = next[idx];
    const aRoll = String(a.roll_number || '').trim();
    const cRoll = String(cur.roll_number || '').trim();
    if (aRoll && aRoll.toUpperCase() !== cRoll.toUpperCase()) {
      next[idx] = { ...cur, roll_number: aRoll };
    }
  }
  return next;
}

// One-time read of the old Approve & Match working copy. Absentee rows and
// roll / name corrections that lived only on that tab are folded into the
// Check rows for the same folder. Check rows win when a roll is already present.
export function absorbLegacyApprove(rows, folderId) {
  let next = Array.isArray(rows) ? rows.slice() : [];
  if (!folderId) return next;
  try {
    const raw = localStorage.getItem('mcqApproveResults');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && String(parsed.folder && parsed.folder.id) === String(folderId)) {
        const extras = [];
        for (const r of parsed.rows || []) {
          const synthetic = String(r.id || '').startsWith('synth-')
            || r.omr_file_name === '(filled)'
            || (r.answers && r.answers._absent);
          if (!synthetic) continue;
          const roll = String(r.roll_number || '').trim().toUpperCase();
          if (!roll) continue;
          if (next.some(m => String(m.roll_number || '').trim().toUpperCase() === roll)) continue;
          extras.push({
            id: r.id || `synth-migrated-${roll}`,
            omr_scan_id: null,
            text_scan_id: null,
            omr_file_name: '(filled)',
            text_file_name: '',
            student_name: r.student_name || '',
            handwritten_paper_variant: r.handwritten_paper_variant || r.paper_variant || 'A',
            handwritten_roll_number: r.handwritten_roll_number || r.roll_number || roll,
            class: r.class || '',
            section: r.section || '',
            subject: r.subject || '',
            roll_number: r.roll_number || roll,
            paper_variant: r.paper_variant || 'A',
            answers: { ...(r.answers || {}), _absent: true }
          });
        }
        if (extras.length) next = next.concat(extras);
      }
    }
  } catch (_) { /* ignore a bad cache */ }

  try {
    const raw = localStorage.getItem('mcqApproveState');
    if (!raw) return next;
    const saved = JSON.parse(raw);
    if (!saved || String(saved.omrFolderId) !== String(folderId)) return next;
    const rollOverrides = saved.rollOverrides || {};
    const nameOverrides = saved.nameOverrides || {};
    next = next.map(r => {
      const id = String(r.id);
      const roll = rollOverrides[id];
      const name = nameOverrides[id];
      if (roll == null && name == null) return r;
      const trimmedRoll = roll == null ? '' : String(roll).trim();
      return {
        ...r,
        roll_number: trimmedRoll !== '' ? trimmedRoll : r.roll_number,
        student_name: name != null ? name : r.student_name
      };
    });
  } catch (_) { /* ignore a bad cache */ }
  return next;
}

export function buildEdupagePayload({ students, match, mcqCount, folderTitle, listLabel }) {
  const studentsPayload = (students || []).map(s => {
    const matched = match.byStudent.get(s.id);
    const cleanName = stripRollFromName(s.student_name);
    const sRoll = extractRollFromName(s.student_name) || s.roll_number || '';
    const trimmedAnswers = {};
    for (let q = 1; q <= mcqCount; q++) {
      const k = String(q);
      const v = matched?.answers?.[k];
      const isBlank = v == null || (typeof v === 'string' && v.trim() === '');
      trimmedAnswers[k] = isBlank ? '.' : v;
    }
    trimmedAnswers[String(mcqCount + 1)] = buildComment(
      cleanName,
      matched?.omr_file_name,
      isAbsent(matched, mcqCount)
    );
    const rawVariant = String(matched?.paper_variant || '');
    const firstVariantLetter = (rawVariant.match(/[A-Za-z]/) || [''])[0].toUpperCase();
    return {
      name: cleanName,
      roll: sRoll,
      variant: firstVariantLetter,
      answers: trimmedAnswers,
      needsEvaluate: matched ? needsEvaluation(matched.answers, mcqCount) : true,
      points: isAbsent(matched, mcqCount) ? 'a' : ''
    };
  });
  return {
    source: 'fastgrader-mcq',
    version: 1,
    generated_at: new Date().toISOString(),
    folder: folderTitle || '',
    list: listLabel || '',
    mcqCount,
    totalCells: mcqCount + 1,
    students: studentsPayload
  };
}

// One student's EduPage cells, without name, roll, or variant.
// Blank answers are "." so a paste keeps each question in its own cell.
// Cells are tab-separated: answers 1..N, comment, points.
export function studentEdupageClipboard({ student, matched, mcqCount }) {
  const count = Number(mcqCount) || 0;
  const cleanName = stripRollFromName(student && student.student_name);
  const cells = [];
  for (let q = 1; q <= count; q++) {
    const v = matched && matched.answers ? matched.answers[String(q)] : null;
    const isBlank = v == null || (typeof v === 'string' && v.trim() === '');
    cells.push(isBlank ? '.' : String(v).trim());
  }
  const absent = isAbsent(matched, count);
  cells.push(buildComment(cleanName, matched && matched.omr_file_name, absent));
  cells.push(absent ? 'a' : '');
  return cells.join('\t');
}
